/**
 * Sidebar Configuration
 *
 * Centralized configuration for sidebar behavior, dropdown IDs,
 * and localStorage keys.
 */

// ============================================
// Dropdown Configuration
// ============================================

/**
 * List of all sidebar dropdown menu IDs.
 * These correspond to the data-collapse-toggle targets in the sidebar template.
 */
export const DROPDOWN_IDS = [
  "dropdown-samai",
  "dropdown-portfolio",
  "dropdown-research",
  "dropdown-scorecards",
  "dropdown-screens",
] as const;

/** Type for valid dropdown IDs */
export type DropdownId = (typeof DROPDOWN_IDS)[number];

/**
 * List of admin sidebar dropdown menu IDs.
 * These correspond to the data-collapse-toggle targets in the custom_admin sidebar template.
 */
export const ADMIN_DROPDOWN_IDS = [
  "dropdown-admin-samai",
  "dropdown-admin-company-embeds",
  "dropdown-admin-scorecards",
  "dropdown-admin-screens",
  "dropdown-admin-teams",
  "dropdown-admin-users",
  "dropdown-admin-ai-settings",
] as const;

/** Type for valid admin dropdown IDs */
export type AdminDropdownId = (typeof ADMIN_DROPDOWN_IDS)[number];

// ============================================
// Storage Keys
// ============================================

/** localStorage keys for sidebar state persistence */
export const STORAGE_KEYS = {
  /** Whether the sidebar is expanded (true) or mini (false) */
  SIDEBAR_EXPANDED: "sidebarExpanded",
  /** Persisted open/close state of each dropdown menu */
  DROPDOWN_STATES: "sidebarDropdownStates",
} as const;

// ============================================
// Timing Constants
// ============================================

/** Delay before closing flyout when mouse leaves (ms) */
export const CLOSE_DELAY_MS = 150;

/** Delay before hiding tooltip (ms) */
export const TOOLTIP_DELAY_MS = 100;

/** Delay for Flowbite to process toggle before checking state (ms) */
export const FLOWBITE_TOGGLE_DELAY_MS = 50;

// ============================================
// Layout Constants
// ============================================

/**
 * Flyout horizontal offset from trigger right edge (px).
 * Creates visual separation between the mini sidebar and the flyout menu.
 */
export const FLYOUT_OFFSET_X = 12;

/**
 * Flyout vertical offset from trigger top (px).
 * Slight upward adjustment to visually align flyout with the trigger icon.
 */
export const FLYOUT_OFFSET_Y = -4;

/**
 * Default flyout left position matching mini sidebar width (px).
 * Calculated as: 4rem (64px mini sidebar) + 4px gap = 68px.
 */
export const DEFAULT_FLYOUT_LEFT = 68;

/**
 * Minimum gap between flyout top and navbar bottom (px).
 * Ensures flyouts don't overlap with the fixed navigation bar.
 */
export const FLYOUT_NAV_GAP = 4;

// ============================================
// CSS Classes
// ============================================

/** CSS classes for sidebar width states */
export const SIDEBAR_WIDTH_CLASSES = {
  EXPANDED: "lg:w-72",
  MINI: "lg:w-16",
} as const;

/** CSS classes for main content margin states */
export const MAIN_CONTENT_MARGIN_CLASSES = {
  EXPANDED: "lg:ml-72",
  MINI: "lg:ml-16",
} as const;

/** CSS classes for bottom menu layout states */
export const BOTTOM_MENU_CLASSES = {
  EXPANDED: ["space-x-4", "p-4"],
  MINI: ["flex-col", "space-y-4", "p-2"],
} as const;

// ============================================
// Breakpoints
// ============================================

/** Large screen breakpoint for mini mode detection */
export const LARGE_SCREEN_BREAKPOINT = "(min-width: 1024px)";
